import { useEffect, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trophy, CalendarDays, Monitor, Maximize } from "lucide-react";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const POLL_INTERVAL = 30000;

// ─── Types (matches get-tv-display edge function response) ────

interface TVDisplayData {
  org: { name: string; logo_url: string | null } | null;
  hall: { name: string; opening_time: string; closing_time: string };
  courts: Array<{ name: string; sort_order: number }>;
  bookings: Array<{
    court_name: string;
    start_time: string;
    end_time: string;
    duration_minutes: number;
    is_open_match: boolean;
  }>;
  tournaments: Array<{
    name: string;
    format: string;
    status: string;
    date: string;
    start_time: string;
  }>;
  leaderboard: Array<{
    rank: number;
    display_name: string;
    points: number;
  }>;
  upcoming_league_matches: Array<{
    league_name: string;
    date: string;
    time: string | null;
  }>;
}

const VIEW_COUNT = 3;
const ROTATE_INTERVAL = 15000;

export default function TVDisplay() {
  const [searchParams] = useSearchParams();
  const hallId = searchParams.get("hall");
  const [now, setNow] = useState(new Date());
  const [activeView, setActiveView] = useState(0);
  const [_isFullscreen, setIsFullscreen] = useState(false);

  // Clock tick every 30s
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  // Auto-rotate
  useEffect(() => {
    const t = setInterval(() => setActiveView((v) => (v + 1) % VIEW_COUNT), ROTATE_INTERVAL);
    return () => clearInterval(t);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  // Single edge function fetch with polling
  const { data } = useQuery({
    queryKey: ["tv-display", hallId],
    queryFn: async () => {
      const res = await fetch(
        `${SUPABASE_URL}/functions/v1/get-tv-display?hall=${encodeURIComponent(hallId!)}`,
      );
      if (!res.ok) return null;
      return res.json() as Promise<TVDisplayData>;
    },
    enabled: !!hallId,
    refetchInterval: POLL_INTERVAL,
  });

  if (!hallId) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center">
        <p className="text-gray-400 text-lg">Hall-parameter saknas. Ange ?hall=&lt;hall-uuid&gt; i URL:en.</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center">
        <p className="text-gray-400 text-lg">Laddar...</p>
      </div>
    );
  }

  const { org, hall, courts, bookings, tournaments, leaderboard, upcoming_league_matches } = data;

  const startHour = parseInt(hall.opening_time?.split(":")[0] || "6");
  const rawEndHour = parseInt(hall.closing_time?.split(":")[0] || "23");
  const endHour = rawEndHour === 0 ? 24 : rawEndHour || 23;
  const hours = Array.from({ length: endHour - startHour }, (_, i) => startHour + i);
  const currentHour = now.getHours();
  const currentMin = now.getMinutes();

  const activeTournament = tournaments.find((t) => t.status === "active");

  return (
    <div className="min-h-screen bg-gray-950 text-white p-6 font-sans select-none">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {org?.logo_url && <img src={org.logo_url} className="h-10 w-10 rounded-lg object-cover" alt="" />}
          <h1 className="text-3xl font-bold tracking-tight">{org?.name || "Padelhall"}</h1>
        </div>
        <div className="flex items-center gap-4">
          {/* View dots */}
          <div className="flex items-center gap-2">
            {Array.from({ length: VIEW_COUNT }).map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveView(i)}
                className={`h-2.5 w-2.5 rounded-full transition-all ${activeView === i ? "bg-white scale-125" : "bg-gray-600"}`}
              />
            ))}
          </div>
          <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white" onClick={toggleFullscreen}>
            <Maximize className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3 text-gray-400">
            <Monitor className="h-5 w-5" />
            <span className="text-2xl font-mono">{format(now, "HH:mm")}</span>
            <span className="text-lg">{format(now, "EEEE d MMMM", { locale: sv })}</span>
          </div>
        </div>
      </div>

      {/* VIEW 0: Schedule */}
      {activeView === 0 && (
        <div className="bg-gray-900 rounded-2xl p-4 overflow-hidden animate-in fade-in duration-500">
          <h2 className="text-xl font-semibold mb-4">Dagens schema</h2>
          <div className="overflow-x-auto">
            <div className="min-w-[800px]">
              <div className="flex border-b border-gray-700 pb-2 mb-2">
                <div className="w-28 shrink-0" />
                {hours.map((h) => (
                  <div key={h} className="flex-1 text-center text-sm text-gray-500 font-mono">{h}:00</div>
                ))}
              </div>
              {courts.map((court, courtIdx) => {
                const courtBookings = bookings.filter((b) => b.court_name === court.name);
                return (
                  <div key={courtIdx} className="flex items-center h-16 border-b border-gray-800">
                    <div className="w-28 shrink-0 text-sm font-semibold text-gray-300 pr-2">{court.name}</div>
                    <div className="flex-1 relative h-12">
                      {courtBookings.map((b, bIdx) => {
                        const bStart = new Date(b.start_time);
                        const bEnd = new Date(b.end_time);
                        const leftPct = ((bStart.getHours() - startHour) + bStart.getMinutes() / 60) / (endHour - startHour) * 100;
                        const widthPct = (b.duration_minutes / 60) / (endHour - startHour) * 100;
                        const isPast = bEnd < now;
                        const isCurrent = bStart <= now && bEnd > now;
                        return (
                          <div
                            key={bIdx}
                            className={`absolute top-1 h-10 rounded-lg flex items-center justify-center text-xs font-medium transition-all ${
                              isCurrent
                                ? "bg-green-500/90 text-white ring-2 ring-green-400 animate-pulse"
                                : isPast
                                ? "bg-gray-700/60 text-gray-500"
                                : b.is_open_match
                                ? "bg-blue-600/80 text-white"
                                : "bg-primary/80 text-white"
                            }`}
                            style={{ left: `${Math.max(0, leftPct)}%`, width: `${widthPct}%`, minWidth: "30px" }}
                          >
                            {isCurrent && "● Pågår"}
                            {b.is_open_match && !isCurrent && "Öppen"}
                          </div>
                        );
                      })}
                      {currentHour >= startHour && currentHour < endHour && (
                        <div
                          className="absolute top-0 h-full w-0.5 bg-red-500 z-10"
                          style={{ left: `${((currentHour - startHour) + currentMin / 60) / (endHour - startHour) * 100}%` }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Stats row */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="bg-gray-800 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-primary">{bookings.length}</p>
              <p className="text-xs text-gray-400">Bokningar idag</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-green-400">
                {bookings.filter((b) => new Date(b.start_time) <= now && new Date(b.end_time) > now).length}
              </p>
              <p className="text-xs text-gray-400">Pågår nu</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-blue-400">
                {bookings.filter((b) => b.is_open_match).length}
              </p>
              <p className="text-xs text-gray-400">Öppna matcher</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-yellow-400">{courts.length}</p>
              <p className="text-xs text-gray-400">Banor</p>
            </div>
          </div>
        </div>
      )}

      {/* VIEW 1: Tournament leaderboard */}
      {activeView === 1 && (
        <div className="bg-gray-900 rounded-2xl p-6 animate-in fade-in duration-500">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Trophy className="h-6 w-6 text-yellow-400" />
            {activeTournament ? activeTournament.name : "Turneringar"}
          </h2>
          {activeTournament && leaderboard.length > 0 ? (
            <div className="space-y-2">
              {leaderboard.map((entry, i) => (
                <div key={i} className="flex items-center gap-4 bg-gray-800 rounded-xl px-6 py-4">
                  <span className={`text-2xl font-bold ${entry.rank <= 3 ? "text-yellow-400" : "text-gray-500"}`}>
                    #{entry.rank}
                  </span>
                  <div className="flex-1">
                    <p className="font-semibold text-lg">{entry.display_name}</p>
                  </div>
                  <span className="text-2xl font-bold text-primary">{entry.points} p</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-gray-400 text-lg">
                {tournaments.length === 0 ? "Inga turneringar planerade" : "Kommande turneringar"}
              </p>
              {tournaments.map((t, i) => (
                <div key={i} className="flex items-center justify-between bg-gray-800 rounded-xl px-6 py-4">
                  <div>
                    <p className="font-semibold text-lg">{t.name}</p>
                    <p className="text-gray-400">{t.format} · {format(new Date(t.date), "d MMMM", { locale: sv })}</p>
                  </div>
                  <Badge className={t.status === "active" ? "bg-green-600 text-white" : "bg-blue-600 text-white"}>
                    {t.status === "active" ? "Pågår" : "Anmälan öppen"}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* VIEW 2: Upcoming events */}
      {activeView === 2 && (
        <div className="bg-gray-900 rounded-2xl p-6 animate-in fade-in duration-500">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-blue-400" />
            Kommande event
          </h2>
          {upcoming_league_matches.length === 0 && tournaments.length === 0 ? (
            <p className="text-gray-400 text-lg text-center py-12">Inga kommande event</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {tournaments.map((t, i) => (
                <div key={`t-${i}`} className="bg-gray-800 rounded-xl px-6 py-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Trophy className="h-4 w-4 text-yellow-400" />
                    <Badge className="bg-yellow-600/30 text-yellow-300 text-xs">Turnering</Badge>
                  </div>
                  <p className="font-semibold text-lg">{t.name}</p>
                  <p className="text-gray-400 text-sm">{format(new Date(t.date), "EEEE d MMMM", { locale: sv })} · {t.start_time?.slice(0, 5)}</p>
                </div>
              ))}
              {upcoming_league_matches.map((m, i) => (
                <div key={`lm-${i}`} className="bg-gray-800 rounded-xl px-6 py-4">
                  <Badge className="bg-blue-600/30 text-blue-300 text-xs mb-1">Serie</Badge>
                  <p className="font-semibold">Serie: {m.league_name}</p>
                  <p className="text-gray-400 text-sm">
                    {format(new Date(m.date), "d MMMM", { locale: sv })} {m.time?.slice(0, 5) || ""}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
